import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  resolveHarvestSwing,
  DEFAULT_HARVEST_KIND,
  type HarvestPropKind as RuleHarvestKind,
} from './matchRules.js';

export type HarvestPropKind = 'barrel' | 'fence' | 'crate';

export type HarvestSwingResult = {
  mats: number;
  hit: boolean;
  hitPoint?: THREE.Vector3;
  propBreak?: { kind: HarvestPropKind; point: THREE.Vector3 };
  botHit?: { damage: number; killed: boolean; name: string; pos: THREE.Vector3 };
  comboHit: 1 | 2 | 3;
  isFinisher: boolean;
};

export type HarvestMeleeBotHit = (
  origin: THREE.Vector3,
  dir: THREE.Vector3,
  comboHit: 1 | 2 | 3,
) => { damage: number; killed: boolean; name: string; pos: THREE.Vector3 } | null;

const COMBO_RESET_S = 1.35;
const COMBO_LEN = 3;

/**
 * Pickaxe-style harvest: short forward ray, impulse on dynamic bodies, breaks after 3 swings.
 * Three-hit combo chain — finisher applies extra structure knockback.
 */
export class FpsHarvest {
  private readonly raycaster = new THREE.Raycaster();
  private comboIndex = 0;
  private comboIdle = 0;

  constructor(
    private readonly camera: THREE.Camera,
    private readonly hitTargets: THREE.Object3D[],
    private readonly getSyncables: () => { mesh: THREE.Object3D; body: CANNON.Body }[],
    private readonly physicsWorld: CANNON.World,
    private readonly meleeBotHit?: HarvestMeleeBotHit,
  ) {}

  tickCombo(dt: number): void {
    if (this.comboIndex <= 0) return;
    this.comboIdle += dt;
    if (this.comboIdle >= COMBO_RESET_S) {
      this.comboIndex = 0;
      this.comboIdle = 0;
    }
  }

  getComboHit(): 1 | 2 | 3 {
    return ((this.comboIndex % COMBO_LEN) + 1) as 1 | 2 | 3;
  }

  /** Raycast from camera center, 3.2m; also checks 2.5m melee cone for bots. */
  swing(): HarvestSwingResult {
    const comboHit = this.getComboHit();
    const isFinisher = comboHit === COMBO_LEN;
    this.comboIndex = isFinisher ? 0 : comboHit;
    this.comboIdle = 0;

    const origin = this.camera.getWorldPosition(new THREE.Vector3());
    const dir = this.camera.getWorldDirection(new THREE.Vector3()).normalize();
    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera);
    this.raycaster.ray.origin.copy(origin);
    this.raycaster.ray.direction.copy(dir);
    this.raycaster.far = 3.2;
    this.raycaster.layers.set(0);

    let mats = 0;
    let hit = false;
    let hitPoint: THREE.Vector3 | undefined;
    let propBreak: HarvestSwingResult['propBreak'];
    const impulseBase = comboHit === 1 ? 34 : comboHit === 2 ? 48 : 78;
    const impulse = isFinisher ? impulseBase * 1.15 : impulseBase;

    const hits = this.raycaster.intersectObjects(this.hitTargets, true);
    if (hits.length > 0) {
      const rayHit = hits[0]!;
      const pt = rayHit.point;
      hitPoint = pt.clone();

      let obj: THREE.Object3D | null = rayHit.object;
      while (obj) {
        const body = obj.userData.cannonBody as CANNON.Body | undefined;
        if (body && body.mass > 0) {
          const lift = isFinisher ? 0.32 : comboHit === 2 ? 0.24 : 0.18;
          body.applyImpulse(
            new CANNON.Vec3(
              dir.x * impulse,
              dir.y * lift * impulse,
              dir.z * impulse,
            ),
            new CANNON.Vec3(pt.x, pt.y, pt.z),
          );

          const ud = obj.userData as { pickHits?: number };
          const tagged = obj.userData.harvestProp as string | undefined;
          // Tagged props keep their kind; untagged mass>0 dynamics default to crate
          const ruleKind: RuleHarvestKind =
            tagged === 'barrel' ||
            tagged === 'fence' ||
            tagged === 'crate' ||
            tagged === 'tree' ||
            tagged === 'rock'
              ? (tagged as RuleHarvestKind)
              : DEFAULT_HARVEST_KIND;
          const swing = resolveHarvestSwing(ud.pickHits ?? 0, ruleKind);
          ud.pickHits = swing.hits;

          if (swing.broken) {
            const breakKind: HarvestPropKind =
              swing.kind === 'barrel' || swing.kind === 'fence' || swing.kind === 'crate'
                ? swing.kind
                : 'crate';
            propBreak = { kind: breakKind, point: pt.clone() };
            this.physicsWorld.removeBody(body);
            obj.parent?.remove(obj);
            this.removeFromSyncables(obj, body);
            mats = swing.mats;
            hit = true;
          } else {
            hit = true;
          }
          break;
        }
        obj = obj.parent;
      }
    }

    let botHit: HarvestSwingResult['botHit'];
    if (this.meleeBotHit) {
      const botRes = this.meleeBotHit(origin, dir, comboHit);
      if (botRes) botHit = botRes;
    }

    return { mats, hit, hitPoint, propBreak, botHit, comboHit, isFinisher };
  }

  private removeFromSyncables(mesh: THREE.Object3D, body: CANNON.Body): void {
    const list = this.getSyncables();
    const i = list.findIndex((s) => s.mesh === mesh && s.body === body);
    if (i >= 0) list.splice(i, 1);
  }
}
